import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { templateProductCode } from '@/lib/templateProductCode'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import {
  Loader2,
  AlertCircle,
  Plus,
  MoreVertical,
  Tag,
  Workflow,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  History,
  Power,
  ArrowUpCircle,
  Info,
  Search,
  X,
  Zap,
  Copy,
  Download,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { useAuth } from '@/providers/AuthProvider'
import { VersionAuditDialog } from '@/components/versioning/VersionAuditDialog'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { CopyProcedureTemplateDialog } from '@/components/form-builder/CopyProcedureTemplateDialog'
import { ImportProcedureTemplateDialog } from '@/components/form-builder/ImportProcedureTemplateDialog'
import { downloadBundle } from '@/lib/procedureTemplateImport'
import { templateExportEnabled } from '@/lib/templateExportGate'
import { useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { formUniqueId, templateFamilyUniqueId, templateUniqueId, versionLabel } from '@/lib/versioning'
import { groupTemplatesByFamily } from '@/lib/procedureTemplateFamilies'
import {
  DEFAULT_FILTERS,
  EMPTY_FILTERS,
  emptyStateMessage,
  filtersRevealing,
  isUnfiltered,
  toListParams,
  type StatusFilter,
  type TemplateFilters,
} from '@/lib/procedureTemplateFilters'
import { FORM_CATEGORY_LABELS, FORM_CATEGORY_OPTIONS, PROCEDURE_TYPE_OPTIONS, PROCEDURE_TYPE_LABELS } from '@/types/cgt'
import type {
  FormCategory,
  ProcedureTemplateDetail,
  ProcedureTemplateListItem,
  ProcedureTemplateSequenceStep,
  ProcedureType,
} from '@/types/cgt'
import { useTenantSigners } from '@/hooks/useSigners'
import { PageHeading } from '@/components/layout/PageHeading'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function StatusBadge({ active }: { active: boolean | undefined }) {
  const isActive = active !== false
  return (
    <Badge
      variant='outline'
      className={`text-[10px] font-semibold uppercase tracking-wide ${
        isActive
          ? 'border-green-200 bg-green-50 text-green-700'
          : 'border-red-200 bg-red-50 text-red-600'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  )
}

// Per-user column layout for the templates list. The expand chevron, Template
// and the actions cell are locked. See src/components/table/index.ts.
const TEMPLATES_TABLE_ID = 'procedure-templates'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): the list pages families, so
// the ids here are the backend's allowed family sorts (FAMILY_SORTS in
// procedure-templates.service), each comparing the family row's newest version.
const TEMPLATES_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Versions', locked: true },
  { id: 'uniqueId', header: 'Unique ID', defaultWidth: 160, sortable: true },
  { id: 'forms', header: 'Forms', defaultWidth: 90, sortable: true },
  { id: 'template', header: 'Template', locked: true, defaultWidth: 240, sortable: true },
  { id: 'type', header: 'Type', defaultWidth: 140, sortable: true },
  { id: 'version', header: 'Version', defaultWidth: 120, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 120, sortable: true },
  { id: 'lastUpdate', header: 'Last Version Update', defaultWidth: 180, sortable: true },
  // 2 icon buttons at h-7 (28px) + 4px gap + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 110 },
]

const TEMPLATE_HEAD_CLASSES: Record<string, string> = {
  expand: 'w-8 px-2 py-3',
  actions: 'px-4 py-3 text-table-header-foreground',
}
const TEMPLATE_HEAD_DEFAULT = 'px-4 py-3 text-left font-medium text-table-header-foreground'

const VERSION_HEAD_CLASSES: Record<string, string> = {
  expand: 'w-8 px-2 py-2',
  uniqueId: 'px-3 py-2 text-left font-medium',
  version: 'px-3 py-2 text-left font-medium',
  forms: 'px-3 py-2 text-left font-medium',
  status: 'px-3 py-2 text-left font-medium',
  autoSelect: 'px-3 py-2 text-left font-medium',
  lastUpdate: 'px-3 py-2 text-left font-medium',
  actions: 'px-3 py-2 text-right font-medium',
}

// The nested version-history table inside an expanded family row, and the
// form-steps table in the detail panel. Separate ids from the main list: they
// are separate tables that happen to open inside it.
const TEMPLATE_VERSIONS_TABLE_ID = 'procedure-template-versions'

const TEMPLATE_VERSIONS_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Forms', locked: true },
  { id: 'uniqueId', header: 'Unique ID', locked: true },
  { id: 'version', header: 'Version' },
  { id: 'forms', header: 'Forms count' },
  { id: 'status', header: 'Status' },
  { id: 'autoSelect', header: 'Auto Select' },
  { id: 'lastUpdate', header: 'Last Update' },
  { id: 'actions', header: 'Actions', locked: true },
]

const TEMPLATE_STEPS_TABLE_ID = 'procedure-template-steps'

const TEMPLATE_STEPS_COLUMNS: TableColumnDef[] = [
  { id: 'formUniqueId', header: 'Form Unique ID', locked: true },
  { id: 'form', header: 'Form', locked: true },
  { id: 'status', header: 'Status' },
  { id: 'versionCheck', header: 'Version Check' },
  { id: 'action', header: 'Action', locked: true },
]

export function ProcedureTemplatesPage() {
  const cols = useTableColumns(TEMPLATES_TABLE_ID, TEMPLATES_COLUMNS)
  const versionCols = useTableColumns(TEMPLATE_VERSIONS_TABLE_ID, TEMPLATE_VERSIONS_COLUMNS)
  // The signer's display name for the audit row. The signature itself is the
  // password the dialog verifies — this is only what the trail shows.
  const { user, email } = useAuth()
  const signerName = user?.fullName ?? email ?? ''

  const navigate = useNavigate()
  const { can } = usePermission()
  // This page renders from THREE places — the standalone /procedure-templates
  // route and both the templates and process tabs of /form-builder — so the
  // origin it hands the detail page is its own live url, tab and all, rather
  // than a constant. Without it the detail page's back link is right one time
  // in three (z8nrz7czdw).
  const withOrigin = useBackNavLinkBuilder('Procedure Templates')
  // The VERSION row being copied. A family has several versions with
  // different flows, so "copy the family" has no well-defined source —
  // the icon lives on the version rows (z8nrz7cz21).
  const [copyTarget, setCopyTarget] = useState<ProcedureTemplateListItem | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // Which version row is mid-export, so its button alone shows the spinner.
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [items, setItems] = useState<ProcedureTemplateListItem[]>([])
  const [total, setTotal] = useState(0)
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  // Every filter is applied server-side — see src/lib/procedureTemplateFilters.ts
  // for why. `searchInput` is what the box shows; `filters.search` is what has
  // actually been queried, so typing does not fire a request per keystroke.
  const [filters, setFilters] = useState<TemplateFilters>(DEFAULT_FILTERS)
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Two-level expansion: families (by familyId) and versions (by version id).
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set())
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set())
  const [details, setDetails] = useState<Record<string, ProcedureTemplateDetail | 'loading' | 'error'>>({})

  // Create-template dialog (unchanged behavior from the old card grid).
  const [dialogOpen, setDialogOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<ProcedureType>('Transplant')
  const [saving, setSaving] = useState(false)

  // Per-row actions menu + the "edit product code" modal.
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [editProc, setEditProc] = useState<ProcedureTemplateListItem | null>(null)
  const [productCodeInput, setProductCodeInput] = useState('')
  // Blank = no default, which is NOT the same as 0 (due the same day).
  const [dueOffsetInput, setDueOffsetInput] = useState('')
  const [savingCode, setSavingCode] = useState(false)

  // "Update Procedure Template to Latest Form" flow (signed).
  const [updateTarget, setUpdateTarget] = useState<{
    proc: ProcedureTemplateListItem
    step: ProcedureTemplateSequenceStep
  } | null>(null)
  const [newVersionInput, setNewVersionInput] = useState('')
  const [updateBusy, setUpdateBusy] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)

  // Signed activate/inactivate flow.
  const [statusTarget, setStatusTarget] = useState<{
    proc: ProcedureTemplateListItem
    toActive: boolean
  } | null>(null)
  // Sign as roster for both signed dialogs — loaded only while one is open.
  const templateSigners = useTenantSigners(
    PERMISSIONS.procedureTemplatesManage,
    !!updateTarget || !!statusTarget,
  )
  const [statusBusy, setStatusBusy] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  const [historyFor, setHistoryFor] = useState<ProcedureTemplateListItem | null>(null)
  const loadHistory = useCallback(
    () => (historyFor ? procedureTemplatesApi.versionAudit(historyFor.id) : Promise.resolve([])),
    [historyFor],
  )

  const [autoSelectBusy, setAutoSelectBusy] = useState<string | null>(null)

  const toggleAutoSelect = async (proc: ProcedureTemplateListItem) => {
    setAutoSelectBusy(proc.id)
    setError(null)
    try {
      await procedureTemplatesApi.update(proc.id, { autoSelect: !proc.autoSelect })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update auto select.')
    } finally {
      setAutoSelectBusy(null)
    }
  }

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  // Reset during render so `load` never runs for the old page under the new order.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    procedureTemplatesApi
      .listPaged({
        skip, take,
        groupByFamily: true,
        sort,
        ...toListParams(filters),
      })
      .then(({ items: rows, total: t }) => {
        setItems(rows)
        setTotal(t)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load procedure templates.'))
      .finally(() => setLoading(false))
  }, [skip, take, filters, sort])

  useEffect(load, [load])

  // Debounce the search box into the queried filters. 300ms is long enough that
  // a typed word is one request, short enough not to feel laggy. Resetting
  // `skip` here and not in the input handler keeps it in the same commit as the
  // term, so only one fetch is issued.
  useEffect(() => {
    if (searchInput === filters.search) return
    const t = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }))
      setSkip(0)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput, filters.search])

  const loadDetail = useCallback((id: string) => {
    setDetails(prev => ({ ...prev, [id]: 'loading' }))
    procedureTemplatesApi
      .get(id)
      .then(detail => setDetails(prev => ({ ...prev, [id]: detail })))
      .catch(() => setDetails(prev => ({ ...prev, [id]: 'error' })))
  }, [])

  const families = useMemo(() => groupTemplatesByFamily(items), [items])

  const toggleFamilyExpand = (familyId: string) => {
    setExpandedFamilies(prev => {
      const next = new Set(prev)
      if (next.has(familyId)) next.delete(familyId)
      else next.add(familyId)
      return next
    })
  }

  const toggleExpand = (id: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
        if (!details[id] || details[id] === 'error') loadDetail(id)
      }
      return next
    })
  }

  const handleCreate = async () => {
    if (!name.trim()) return
    setSaving(true)
    setError(null)
    try {
      const created = await procedureTemplatesApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        type,
      })
      setDialogOpen(false)
      setName('')
      setDescription('')
      setType('Transplant')
      navigate(withOrigin(`/procedure-templates/${created.id}/sequence`))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create procedure.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTemplateSettings = async () => {
    if (!editProc) return
    setSavingCode(true)
    setError(null)
    try {
      const trimmed = productCodeInput.trim()
      const offset = dueOffsetInput.trim()
      await procedureTemplatesApi.update(editProc.id, {
        productCode: trimmed || null,
        // Blank clears the default so the template stops deriving; 0 is a real
        // value meaning "due the same day", which is why this is not `|| null`.
        dueOffsetDays: offset === '' ? null : Number(offset),
      })
      setEditProc(null)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update the template.')
    } finally {
      setSavingCode(false)
    }
  }

  const openUpdateModal = (proc: ProcedureTemplateListItem, step: ProcedureTemplateSequenceStep) => {
    setUpdateError(null)
    setNewVersionInput(String((proc.version ?? 1) + 1))
    setUpdateTarget({ proc, step })
  }

  // The dialog hands back a signing token, not a name: WHO signed is the
  // authenticated account, and only the password proves it is them.
  const confirmUpdateToLatest = async (signingToken: string) => {
    if (!updateTarget?.step.latestActiveForm) return
    const version = parseInt(newVersionInput, 10)
    setUpdateBusy(true)
    setUpdateError(null)
    try {
      await procedureTemplatesApi.createVersion(updateTarget.proc.id, {
        oldFormDefinitionId: updateTarget.step.formDefinitionId,
        newFormDefinitionId: updateTarget.step.latestActiveForm.id,
        ...(Number.isFinite(version) ? { version } : {}),
        signingToken,
      })
      setUpdateTarget(null)
      setNotice(
        `${templateUniqueId(updateTarget.proc)} updated: a new template version now uses the latest form version.`,
      )
      setDetails({})
      setExpandedVersions(new Set())
      setExpandedFamilies(new Set())
      load()
    } catch (e) {
      setUpdateError(e instanceof Error ? e.message : 'Failed to update the template.')
    } finally {
      setUpdateBusy(false)
    }
  }

  const confirmStatusChange = async (signingToken: string) => {
    if (!statusTarget) return
    setStatusBusy(true)
    setStatusError(null)
    try {
      const { cascadedForms } = await procedureTemplatesApi.setStatus(statusTarget.proc.id, {
        active: statusTarget.toActive,
        signingToken,
      })
      const verb = statusTarget.toActive ? 'activated' : 'inactivated'
      setNotice(
        `${templateUniqueId(statusTarget.proc)} ${verb}.` +
          (cascadedForms.length > 0
            ? ` Also inactivated ${cascadedForms.length} form version${cascadedForms.length === 1 ? '' : 's'} linked only to this template: ${cascadedForms
                .map(f => `${f.name} ${versionLabel(f.version)}`)
                .join(', ')}.`
            : ''),
      )
      setStatusTarget(null)
      // Clear BOTH caches — leaving `expanded` populated with an empty details
      // map would strand those rows on a spinner (nothing re-triggers loadDetail).
      setDetails({})
      setExpandedVersions(new Set())
      setExpandedFamilies(new Set())
      load()
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Failed to change the template status.')
    } finally {
      setStatusBusy(false)
    }
  }

  /**
   * One template row's actions, shared by the family row and each version row.
   *
   * The family row used to render an EMPTY cell: every control lived on the
   * version rows, which exist only once a family is expanded. So the Actions
   * column was blank on every row you could actually see, and a template's own
   * actions were reachable only by discovering that the row expands. The family
   * row already summarises its LATEST version — name, product code, status — so
   * it now offers that version's actions too.
   *
   * The menu is keyed by `menuKey` rather than the template id because an
   * expanded family shows the same version twice, once as the family row and
   * once as the first version row. Keying on the id alone opened both menus.
   */
  const renderTemplateActions = (
    v: ProcedureTemplateListItem,
    {
      menuKey,
      siblingCount,
      opensUp = false,
    }: { menuKey: string; siblingCount: number; opensUp?: boolean },
  ) => {
    const inactive = v.active === false
    const menuOpen = menuOpenId === menuKey
    return (
      <>
      {can(PERMISSIONS.procedureTemplatesManage) && (
        <Button
          variant='ghost'
          size='icon'
          className='mr-1 h-7 w-7 text-muted-foreground opacity-60 hover:opacity-100'
          aria-label={`Copy ${v.name} ${versionLabel(v.version)}`}
          title='Copy this version and all of its forms into a new template'
          onClick={e => { e.stopPropagation(); setCopyTarget(v) }}
        >
          <Copy className='h-3.5 w-3.5' />
        </Button>
      )}
      {templateExportEnabled && can(PERMISSIONS.procedureTemplatesManage) && (
        <Button
          variant='ghost'
          size='icon'
          className='mr-1 h-7 w-7 text-muted-foreground opacity-60 hover:opacity-100'
          aria-label={`Export ${v.name} ${versionLabel(v.version)}`}
          title='Download this version as a bundle to import into another environment'
          disabled={exportingId === v.id}
          onClick={e => { e.stopPropagation(); void handleExport(v) }}
        >
          {exportingId === v.id
            ? <Loader2 className='h-3.5 w-3.5 animate-spin' />
            : <Download className='h-3.5 w-3.5' />}
        </Button>
      )}
      <div className='relative inline-block'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 text-muted-foreground opacity-60 hover:opacity-100'
          aria-label='Actions'
          onClick={e => { e.stopPropagation(); setMenuOpenId(id => (id === menuKey ? null : menuKey)) }}
        >
          <MoreVertical className='h-4 w-4' />
        </Button>
        {menuOpen && (
          <>
            <div className='fixed inset-0 z-40' onClick={() => setMenuOpenId(null)} />
            <div
              className={`absolute right-0 z-50 w-52 overflow-hidden rounded-md border bg-popover py-1 text-left text-sm shadow-md ${
                opensUp ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
              onClick={e => e.stopPropagation()}
            >
              <button
                className='flex w-full items-center px-3 py-2 hover:bg-muted'
                onClick={() => {
                  setMenuOpenId(null)
                  navigate(withOrigin(`/procedure-templates/${v.id}/sequence`))
                }}
              >
                <Workflow className='mr-2 h-4 w-4' />
                Edit sequence
              </button>
              <button
                className='flex w-full items-center px-3 py-2 hover:bg-muted'
                onClick={() => {
                  setMenuOpenId(null)
                  setEditProc(v)
                  setProductCodeInput(v.productCode ?? '')
                  setDueOffsetInput(
                    v.dueOffsetDays == null
                      ? ''
                      : String(v.dueOffsetDays),
                  )
                }}
              >
                <Tag className='mr-2 h-4 w-4' />
                Edit product code
              </button>
              <button
                className='flex w-full items-center px-3 py-2 hover:bg-muted'
                onClick={() => {
                  setMenuOpenId(null)
                  setHistoryFor(v)
                }}
              >
                <History className='mr-2 h-4 w-4' />
                Version history
              </button>
              {siblingCount > 1 && (
                <button
                  className={`flex w-full items-center px-3 py-2 hover:bg-muted ${v.autoSelect ? 'text-amber-700' : 'text-violet-700'}`}
                  disabled={autoSelectBusy === v.id}
                  onClick={() => {
                    setMenuOpenId(null)
                    toggleAutoSelect(v)
                  }}
                >
                  <Zap className='mr-2 h-4 w-4' />
                  {v.autoSelect ? 'Remove auto select' : 'Set as auto select'}
                </button>
              )}
              <button
                className={`flex w-full items-center px-3 py-2 hover:bg-muted ${inactive ? 'text-green-700' : 'text-destructive'}`}
                onClick={() => {
                  setMenuOpenId(null)
                  setStatusError(null)
                  setStatusTarget({ proc: v, toActive: v.active === false })
                }}
              >
                <Power className='mr-2 h-4 w-4' />
                {inactive ? 'Activate template' : 'Inactivate template'}
              </button>
            </div>
          </>
        )}
      </div>
      </>
    )
  }

  const proposedVersionValid = (() => {
    if (!updateTarget) return false
    const v = parseInt(newVersionInput, 10)
    return Number.isFinite(v) && v > (updateTarget.proc.version ?? 1)
  })()

  /**
   * Download this version as a portable bundle.
   *
   * `warnings` is surfaced as a notice rather than blocking: a requirement
   * whose material was deleted is a dangling soft ref the author has to fix in
   * the source environment, and refusing the download would not help them do it.
   */
  async function handleExport(v: ProcedureTemplateListItem) {
    setExportingId(v.id)
    setError(null)
    try {
      const result = await procedureTemplatesApi.exportBundle(v.id)
      downloadBundle(result.fileName, result.bundle)
      setNotice(
        result.warnings.length > 0
          ? `${result.fileName} downloaded. ${result.warnings.join(' ')}`
          : `${result.fileName} downloaded.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That template could not be exported.')
    } finally {
      setExportingId(null)
    }
  }

  return (
    <div className='w-full px-6 py-8'>
      <div className='mb-6 flex items-center justify-between'>
        <PageHeading title='Procedure Templates' description='Step 3 of setup: wire forms into a procedure workflow. Expand a template to see its forms and adopt newer form versions.' />
        <div className='flex items-center gap-2'>
          {can(PERMISSIONS.procedureTemplatesManage) && (
            <Button variant='outline' onClick={() => setImportOpen(true)}>
              <Upload className='mr-2 h-4 w-4' />
              Import
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className='mr-2 h-4 w-4' />
            New Template
          </Button>
        </div>
      </div>

      {error && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          {error}
        </div>
      )}

      {notice && (
        <div className='mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800'>
          <Info className='mt-0.5 h-4 w-4 shrink-0' />
          <span className='flex-1'>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label='Dismiss'>
            <X className='h-4 w-4 opacity-60 hover:opacity-100' />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className='mb-5 flex flex-wrap items-center gap-2'>
        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder='Search by name, unique ID, or product code…'
            aria-label='Search procedure templates'
            className='w-96 pl-8 pr-8'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          )}
        </div>

        <Select
          value={filters.status}
          onValueChange={v => {
            setFilters(prev => ({ ...prev, status: v as StatusFilter }))
            setSkip(0)
          }}
        >
          <SelectTrigger className='w-44' aria-label='Filter by status'>
            <SelectValue placeholder='Any status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>Any status</SelectItem>
            <SelectItem value='active'>Active</SelectItem>
            <SelectItem value='inactive'>Inactive</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.type}
          onValueChange={v => {
            setFilters(prev => ({ ...prev, type: v as ProcedureType | 'all' }))
            // Page 3 of "all types" is rarely a valid page of one type — reset
            // in the same batch as the filter so only one fetch is issued.
            setSkip(0)
          }}
        >
          <SelectTrigger className='w-56' aria-label='Filter by type'>
            <SelectValue placeholder='All types' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All types</SelectItem>
            {PROCEDURE_TYPE_OPTIONS.map(t => (
              <SelectItem key={t} value={t}>
                {PROCEDURE_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Templates that include at least one form of this Form Type, e.g.
            Transplant templates with a Collection form (z8nrz7f9j7). */}
        <Select
          value={filters.formType}
          onValueChange={v => {
            setFilters(prev => ({ ...prev, formType: v as FormCategory | 'all' }))
            setSkip(0)
          }}
        >
          <SelectTrigger className='w-56' aria-label='Filter by form type'>
            <SelectValue placeholder='All form types' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All form types</SelectItem>
            {FORM_CATEGORY_OPTIONS.map(c => (
              <SelectItem key={c} value={c}>
                {FORM_CATEGORY_LABELS[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className='flex items-center justify-center py-20'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : total === 0 && !isUnfiltered(filters) ? (
        // Distinct from the first-run copy below: with a filter on, zero rows
        // means "nothing matches", not "nothing exists yet" — offering
        // "Create one" here would be the wrong next step. The message names
        // every active filter so a typo reads differently from an empty tenant.
        <div className='rounded-lg border border-dashed py-16 text-center text-muted-foreground'>
          <Workflow className='mx-auto mb-3 h-8 w-8 opacity-40' />
          <p className='text-sm'>{emptyStateMessage(filters)}</p>
          <Button
            variant='outline'
            size='sm'
            className='mt-4'
            onClick={() => {
              setSearchInput('')
              setFilters(EMPTY_FILTERS)
              setSkip(0)
            }}
          >
            Clear filters
          </Button>
        </div>
      ) : total === 0 ? (
        <div className='rounded-lg border border-dashed py-16 text-center text-muted-foreground'>
          <Workflow className='mx-auto mb-3 h-8 w-8 opacity-40' />
          <p className='text-sm'>No procedure templates yet. Create one to start defining a form flow.</p>
        </div>
      ) : (
        <>
        <div className='overflow-hidden rounded-lg border'>
          <table className='w-full text-sm'>
            <thead className='border-b bg-table-header'>
              <tr>
                {cols.visible.map(({ def }) => (
                  <ResizableTh
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={TEMPLATE_HEAD_CLASSES[def.id] ?? TEMPLATE_HEAD_DEFAULT}
                    resizable={def.id !== 'expand' && def.id !== 'actions'}
                  >
                    {def.id === 'expand' ? null : def.header}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {families.map((family, fi) => {
                const { latest, versions } = family
                const isFamilyExpanded = expandedFamilies.has(family.familyId)
                return (
                  <Fragment key={family.familyId}>
                    <tr
                      className={`${fi % 2 === 0 ? 'bg-background' : 'bg-muted/20'} cursor-pointer hover:bg-accent/40`}
                      onClick={() => toggleFamilyExpand(family.familyId)}
                    >
                      <ColumnCells
                        columns={cols}
                        cells={{
                          expand: (
                            <td className='px-2 py-3'>
                              <button
                                onClick={e => { e.stopPropagation(); toggleFamilyExpand(family.familyId) }}
                                aria-label={isFamilyExpanded ? 'Collapse versions' : 'Expand versions'}
                                className='rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground'
                              >
                                {isFamilyExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
                              </button>
                            </td>
                          ),
                          uniqueId: (
                            <td className='px-4 py-3'>
                              <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                                {templateFamilyUniqueId(family.familyId)}
                              </code>
                            </td>
                          ),
                          forms: (
                            <td className='px-4 py-3 text-muted-foreground'>
                              {latest._count?.sequenceSteps ?? 0}
                            </td>
                          ),
                          template: (
                            <td className='px-4 py-3'>
                              <span className='font-medium'>{latest.name}</span>
                              {/* Only a product code the template actually has
                                  (z8nrz7dp73). Most templates never carry one, so
                                  an absent — or blank — code renders nothing: no
                                  "No product code" line reporting it on every row. */}
                              {templateProductCode(latest) && (
                                <p className='text-[11px] text-muted-foreground'>
                                  <span className='font-mono'>{templateProductCode(latest)}</span>
                                </p>
                              )}
                            </td>
                          ),
                          type: (
                            <td className='px-4 py-3 text-muted-foreground'>
                              {latest.type ? PROCEDURE_TYPE_LABELS[latest.type] : '—'}
                            </td>
                          ),
                          version: (
                            <td className='px-4 py-3'>
                              <Badge variant='outline' className='text-xs'>
                                {versions.length} version{versions.length !== 1 ? 's' : ''}
                              </Badge>
                            </td>
                          ),
                          status: (
                            <td className='px-4 py-3'>
                              <StatusBadge active={latest.active} />
                            </td>
                          ),
                          lastUpdate: (
                            <td className='px-4 py-3 text-xs text-muted-foreground'>
                              {latest.lastVersionUpdateAt
                                ? new Date(latest.lastVersionUpdateAt).toLocaleDateString()
                                : '—'}
                            </td>
                          ),
                          // The row summarises `latest`, so it offers that
                          // version's actions — see renderTemplateActions.
                          actions: (
                            <td className='px-4 py-3 text-right'>
                              {renderTemplateActions(latest, {
                                menuKey: `family:${family.familyId}`,
                                siblingCount: versions.length,
                              })}
                            </td>
                          ),
                        }}
                      />
                    </tr>
                    {isFamilyExpanded && (
                      <tr className={fi % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td />
                        <td colSpan={Math.max(1, cols.visible.length - 1)} className='px-4 pb-4'>
                          <div className='rounded-lg border bg-card'>
                            <p className='border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
                              Versions of {latest.name}
                            </p>
                            <table className='w-full text-sm'>
                              <thead>
                                <tr className='border-b text-[11px] uppercase tracking-wide text-muted-foreground'>
                                  {versionCols.visible.map(({ def }) => (
                                    <ResizableTh
                                      key={def.id}
                                      columnId={def.id}
                                      columns={versionCols}
                                      label={def.header}
                                      className={VERSION_HEAD_CLASSES[def.id]}
                                      resizable={def.id !== 'expand' && def.id !== 'actions'}
                                    >
                                      {def.id === 'expand' || def.id === 'actions'
                                        ? null
                                        : def.header}
                                    </ResizableTh>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {versions.map((v, vi) => {
                                  const isVersionExpanded = expandedVersions.has(v.id)
                                  const detail = details[v.id]
                                  const inactive = v.active === false
                                  // The open-menu check moved into
                                  // renderTemplateActions; only the direction the menu
                                  // opens depends on this row's position in the list.
                                  const menuOpensUp = versions.length - 1 - vi < 4 && vi >= 4
                                  return (
                                    <Fragment key={v.id}>
                                      <tr
                                        className={`border-b last:border-b-0 cursor-pointer hover:bg-accent/40 ${(v.autoSelect || versions.length === 1) ? 'bg-green-50/60 dark:bg-green-950/20' : ''}`}
                                        onClick={() => navigate(withOrigin(`/procedure-templates/${v.id}/sequence`))}
                                      >
                                        <ColumnCells
                                          columns={versionCols}
                                          cells={{
                                            expand: (
                                              <td className='px-2 py-2'>
                                                <button
                                                  onClick={e => { e.stopPropagation(); toggleExpand(v.id) }}
                                                  aria-label={isVersionExpanded ? 'Collapse forms' : 'Expand forms'}
                                                  className='rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground'
                                                >
                                                  {isVersionExpanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
                                                </button>
                                              </td>
                                            ),
                                            uniqueId: (
                                              <td className='px-3 py-2'>
                                                <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                                                  {templateUniqueId(v)}
                                                </code>
                                              </td>
                                            ),
                                            version: (
                                              <td className='px-3 py-2'>
                                                <span className={inactive ? 'text-muted-foreground' : ''}>
                                                  {versionLabel(v.version)}
                                                </span>
                                              </td>
                                            ),
                                            forms: (
                                              <td className='px-3 py-2 text-muted-foreground'>
                                                {v._count?.sequenceSteps ?? 0}
                                              </td>
                                            ),
                                            status: (
                                              <td className='px-3 py-2'>
                                                <StatusBadge active={v.active} />
                                              </td>
                                            ),
                                            autoSelect: (
                                              <td className='px-3 py-2'>
                                                {(v.autoSelect || versions.length === 1) && (
                                                  <Badge
                                                    variant='outline'
                                                    className='border-green-200 bg-green-50 text-[10px] font-semibold uppercase tracking-wide text-green-700'
                                                  >
                                                    <Zap className='mr-1 h-3 w-3' />
                                                    Auto
                                                  </Badge>
                                                )}
                                              </td>
                                            ),
                                            lastUpdate: (
                                              <td className='px-3 py-2 text-xs text-muted-foreground'>
                                                {v.lastVersionUpdateAt
                                                  ? new Date(v.lastVersionUpdateAt).toLocaleDateString()
                                                  : '—'}
                                              </td>
                                            ),
                                            actions: (
                                              <td className='px-3 py-2 text-right'>
                                                {renderTemplateActions(v, {
                                                  menuKey: v.id,
                                                  siblingCount: versions.length,
                                                  opensUp: menuOpensUp,
                                                })}
                                              </td>
                                            ),
                                          }}
                                        />
                                      </tr>
                                      {isVersionExpanded && (
                                        <tr className='border-b last:border-b-0'>
                                          <td />
                                          <td colSpan={Math.max(1, versionCols.visible.length - 1)} className='px-3 pb-3'>
                                            <FormsPanel
                                              proc={v}
                                              detail={detail}
                                              onRetryDetail={() => loadDetail(v.id)}
                                              onUpdateToLatest={step => openUpdateModal(v, step)}
                                            />
                                          </td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
          <div className='flex items-center justify-between border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground'>
            <span>
              {total > 0
                ? `${skip + 1}–${Math.min(skip + take, total)} of ${total} templates`
                : '0 templates'}
            </span>
            <div className='flex items-center gap-2'>
              <select
                value={take}
                onChange={e => {
                  setTake(Number(e.target.value))
                  setSkip(0)
                }}
                className='rounded border bg-background px-2 py-0.5 text-xs'
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <div className='flex items-center gap-1'>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip === 0}
                  onClick={() => setSkip(s => Math.max(0, s - take))}
                >
                  <ChevronLeft className='h-3.5 w-3.5' />
                  Previous
                </button>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip + take >= total}
                  onClick={() => setSkip(s => s + take)}
                >
                  Next
                  <ChevronRight className='h-3.5 w-3.5' />
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Update Procedure Template to Latest Form — signed */}
      <SignatureDialog
        open={!!updateTarget}
        onOpenChange={open => !open && !updateBusy && setUpdateTarget(null)}
        title='Update Procedure Template to Latest Form'
        confirmLabel='Confirm & Sign'
        confirmDisabled={!proposedVersionValid}
        busy={updateBusy}
        error={updateError}
        signerName={signerName}
        signAs={{
          mode: 'optional',
          options: templateSigners.data,
          loading: templateSigners.isLoading,
          error: templateSigners.isError ? 'Could not load the list of signers.' : null,
          selfId: user?.userId ?? null,
        }}
        onConfirm={confirmUpdateToLatest}
      >
        {updateTarget && (
          <div className='space-y-3 text-sm'>
            <p>
              Template{' '}
              <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                {templateUniqueId(updateTarget.proc)}
              </code>{' '}
              will replace{' '}
              <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                {formUniqueId({
                  id: updateTarget.step.formDefinitionId,
                  familyId: updateTarget.step.formFamilyId,
                  version: updateTarget.step.formVersion,
                })}
              </code>{' '}
              with{' '}
              <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                {updateTarget.step.latestActiveForm
                  ? formUniqueId({
                      id: updateTarget.step.latestActiveForm.id,
                      familyId: updateTarget.step.formFamilyId,
                      version: updateTarget.step.latestActiveForm.version,
                    })
                  : ''}
              </code>
              . This creates a new template version — the current one stays intact.
            </p>
            <div className='flex items-center gap-2'>
              <Label htmlFor='tpl-new-version' className='shrink-0 text-xs'>
                Template version: <strong>{versionLabel(updateTarget.proc.version)}</strong> →
              </Label>
              <div className='flex items-center gap-1'>
                <span className='text-xs font-semibold'>V</span>
                <Input
                  id='tpl-new-version'
                  className='h-8 w-20'
                  inputMode='numeric'
                  value={newVersionInput}
                  onChange={e => setNewVersionInput(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </div>
              <span className='text-[11px] text-muted-foreground'>
                (auto-generated from previous version; editable)
              </span>
            </div>
            {!proposedVersionValid && (
              <p className='text-xs text-destructive'>
                Version must be a number greater than {versionLabel(updateTarget.proc.version)}.
              </p>
            )}
          </div>
        )}
      </SignatureDialog>

      {/* Activate / inactivate — signed */}
      <SignatureDialog
        open={!!statusTarget}
        onOpenChange={open => !open && !statusBusy && setStatusTarget(null)}
        title={statusTarget?.toActive ? 'Activate Procedure Template' : 'Inactivate Procedure Template'}
        description={
          statusTarget?.toActive
            ? `“${statusTarget?.proc.name}” ${versionLabel(statusTarget?.proc.version)} will become available again for new procedures.`
            : `“${statusTarget?.proc.name}” ${versionLabel(statusTarget?.proc.version)} will be inactivated. Form versions linked ONLY to this template are inactivated with it; forms shared with other active templates stay active.`
        }
        confirmLabel={statusTarget?.toActive ? 'Activate & Sign' : 'Inactivate & Sign'}
        busy={statusBusy}
        error={statusError}
        signerName={signerName}
        signAs={{
          mode: 'optional',
          options: templateSigners.data,
          loading: templateSigners.isLoading,
          error: templateSigners.isError ? 'Could not load the list of signers.' : null,
          selfId: user?.userId ?? null,
        }}
        onConfirm={confirmStatusChange}
      />

      <ImportProcedureTemplateDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={created => {
          setImportOpen(false)
          setNotice(`${templateUniqueId(created)} imported.`)
          // Same reasoning as the copy handler below: an import creates a row
          // on a page this one may not be showing, and leaving `expanded`
          // populated against an empty details map strands rows on a spinner.
          setDetails({})
          setExpandedVersions(new Set())
          setExpandedFamilies(new Set())
          const revealing = filtersRevealing(created.name)
          setSearchInput(revealing.search)
          setFilters(revealing)
          setSkip(0)
        }}
      />

      <CopyProcedureTemplateDialog
        open={!!copyTarget}
        source={copyTarget}
        onOpenChange={open => !open && setCopyTarget(null)}
        onCreated={created => {
          setCopyTarget(null)
          setNotice(
            `${templateUniqueId(created)} created — a copy of ${copyTarget ? templateUniqueId(copyTarget) : 'the template'} with its own forms.`,
          )
          // The copy is a NEW family, so it belongs on a page this one may not
          // be showing. Clear both caches for the reason confirmStatusChange
          // does: `expanded` left populated with an empty details map strands
          // those rows on a spinner, since nothing re-triggers loadDetail.
          setDetails({})
          setExpandedVersions(new Set())
          setExpandedFamilies(new Set())
          // NOT a stale-list fix: the page already refetched here, but it
          // refetched the page the user was on, and the list is paged over
          // families sorted by name — so the copy landed on some other page and
          // was silently absent. (A browser reload only appeared to fix it
          // because it resets `skip` to 0.) Searching for the new name is what
          // makes this deterministic; page 1 alone is still the wrong page once
          // a tenant has more templates than fit on it.
          //
          // Setting the filters re-triggers `load` through `useEffect(load, [load])`
          // — calling load() as well would fetch twice (z8nrz7czh6).
          const revealing = filtersRevealing(created.name)
          setSearchInput(revealing.search)
          setFilters(revealing)
          setSkip(0)
        }}
      />

      <VersionAuditDialog
        open={!!historyFor}
        onOpenChange={open => !open && setHistoryFor(null)}
        title={`Version history — ${historyFor ? templateUniqueId(historyFor) : ''}`}
        load={loadHistory}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New procedure template</DialogTitle>
            <DialogDescription>
              Give the procedure workflow a name. You’ll define its form sequence next.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='proc-name'>Name</Label>
              <Input
                id='proc-name'
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='e.g. Autologous CAR-T'
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreate()
                }}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='proc-desc'>Description (optional)</Label>
              <Textarea
                id='proc-desc'
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder='Short description of this workflow'
                rows={3}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='proc-type'>Type</Label>
              <Select value={type} onValueChange={v => setType(v as ProcedureType)}>
                <SelectTrigger id='proc-type'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROCEDURE_TYPE_OPTIONS.map(t => (
                    <SelectItem key={t} value={t}>
                      {PROCEDURE_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving || !name.trim()}>
              {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editProc} onOpenChange={o => !o && setEditProc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit template settings</DialogTitle>
            <DialogDescription>
              Defaults for
              {editProc ? ` “${editProc.name}”` : ' this procedure'} and every run started from
              it.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='proc-product-code'>Product code</Label>
              <Input
                id='proc-product-code'
                value={productCodeInput}
                onChange={e => setProductCodeInput(e.target.value)}
                placeholder='e.g. AUTO-PBSC'
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveTemplateSettings()
                }}
              />
              <p className='text-xs text-muted-foreground'>
                Matches inbound patient events to this template. Blank to clear.
              </p>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='proc-due-offset'>Due offset</Label>
              <div className='flex items-center gap-2'>
                <Input
                  id='proc-due-offset'
                  type='number'
                  min={0}
                  max={3650}
                  step={1}
                  value={dueOffsetInput}
                  onChange={e => setDueOffsetInput(e.target.value)}
                  placeholder='—'
                  className='w-24'
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveTemplateSettings()
                  }}
                />
                <span className='text-sm text-muted-foreground'>days after the scheduled start</span>
              </div>
              {/* Zero is a real value here, so the empty state has to be named
                  explicitly — otherwise a blank field reads as "due same day". */}
              <p className='text-xs text-muted-foreground'>
                Setting a run&apos;s scheduled start fills its due date from this, keeping the time
                of day. Overrides the organisation default in Configure → General; leave blank to
                use that instead. A due date set by hand always wins.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setEditProc(null)} disabled={savingCode}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplateSettings} disabled={savingCode}>
              {savingCode && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function FormsPanel({
  proc,
  detail,
  onRetryDetail,
  onUpdateToLatest,
}: {
  proc: ProcedureTemplateListItem
  detail: ProcedureTemplateDetail | 'loading' | 'error' | undefined
  onRetryDetail: () => void
  onUpdateToLatest: (step: ProcedureTemplateSequenceStep) => void
}) {
  const stepCols = useTableColumns(TEMPLATE_STEPS_TABLE_ID, TEMPLATE_STEPS_COLUMNS)
  const inactive = proc.active === false
  return (
    <div className='rounded-lg border bg-card'>
      <p className='border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
        Forms linked to {templateUniqueId(proc)}
      </p>
      {detail === 'loading' || detail === undefined ? (
        <div className='flex items-center justify-center py-6'>
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        </div>
      ) : detail === 'error' ? (
        <div className='flex items-center gap-2 px-3 py-3 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          Failed to load the template's forms.
          <Button size='sm' variant='outline' onClick={onRetryDetail}>
            Retry
          </Button>
        </div>
      ) : detail.steps.length === 0 ? (
        <p className='px-3 py-4 text-sm text-muted-foreground'>
          No forms in this template yet — add them in the sequence editor.
        </p>
      ) : (
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b text-[11px] uppercase tracking-wide text-muted-foreground'>
              {stepCols.visible.map(({ def }) => (
                <ResizableTh
                  key={def.id}
                  columnId={def.id}
                  columns={stepCols}
                  label={def.header}
                  className={
                    def.id === 'action'
                      ? 'px-3 py-2 text-right font-medium'
                      : 'px-3 py-2 text-left font-medium'
                  }
                >
                  {def.header}
                </ResizableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.steps.map(step => {
              const hasNewer = !!step.latestActiveForm
              return (
                <tr key={step.stepId} className='border-b last:border-b-0'>
                  <ColumnCells
                    columns={stepCols}
                    cells={{
                      formUniqueId: (
                        <td className='px-3 py-2'>
                          <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                            {formUniqueId({
                              id: step.formDefinitionId,
                              familyId: step.formFamilyId,
                              version: step.formVersion,
                            })}
                          </code>
                        </td>
                      ),
                      form: <td className='px-3 py-2'>{step.formName}</td>,
                      status: (
                        <td className='px-3 py-2'>
                          <StatusBadge active={step.formActive} />
                        </td>
                      ),
                      versionCheck: (
                        <td className='px-3 py-2'>
                          {hasNewer ? (
                            <span className='inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800'>
                              <AlertCircle className='h-3.5 w-3.5' />
                              New form version available:{' '}
                              {formUniqueId({
                                id: step.latestActiveForm!.id,
                                familyId: step.formFamilyId,
                                version: step.latestActiveForm!.version,
                              })}
                            </span>
                          ) : (
                            <span className='text-xs text-muted-foreground'>Latest version in use</span>
                          )}
                        </td>
                      ),
                      action: (
                        <td className='px-3 py-2 text-right'>
                          {hasNewer && !inactive && (
                            <Button size='sm' variant='outline' onClick={() => onUpdateToLatest(step)}>
                              <ArrowUpCircle className='mr-1.5 h-3.5 w-3.5' />
                              Update to Latest Form
                            </Button>
                          )}
                        </td>
                      ),
                    }}
                  />
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
